import { BaseMsgProps } from './types';
import { TextMsg } from './TextMsg';
import { useTranslate } from '@tolgee/react';
import { reverse, uniq } from 'lodash-es';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { Button } from 'antd';
import styled from '@emotion/styled';
import { LineWrapper, MessageBody } from 'components/Dialogue';
import { memo, useMemo } from 'react';

export function useNouseMessageTypeLabelMap() {
  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const typeLabelMap = useMemo(() => {
    return {
      SELLER_CENTER_AUTO_REPLY: t('卖家中心自动回复'),
      WELCOME_REPLY: t('aide.进店欢迎语'),
      TIMEOUT_AUTO_REPLY: t('nav.autoReply'),
      ORDER_REMINDER: t('aide.催下单'),
      PAYMENT_REMINDER: t('marketing.催付款'),
      LOGISTICS_CARE: t('aide.物流呵护'),
      POSITIVE_REVIEW_CARE: t('aide.好评关怀'),
      WATCH_LIMIT:
        (
          {
            'zh-CN': 'AI值守兜底',
            'en-US': 'AI On-duty reply',
          } as Partial<Record<General.SystemLanguage, string>>
        )[systemLang] || t('aide.值守兜底'),
      PROACTIVELY_CONTACT_BUYER: t('主动联系买家'),
      PRAISE_REMINDER: t('marketing.催好评'),
    } as const;
  }, [t, systemLang]);

  return {
    t,
    systemLang,
    typeLabelMap,
  };
}

function FoldMsg({
  raw,
  msg,
  conversation,
  nouseMessageList,
  onExpand,
}: BaseMsgProps & {
  nouseMessageList: Conversation.NouseMessage[];
  onExpand: () => void;
}) {
  const { t, typeLabelMap } = useNouseMessageTypeLabelMap();

  const typeList = reverse(uniq(nouseMessageList.map((_) => _.type)));

  const tips =
    nouseMessageList.length === 1
      ? t('fold-message-tips1', { x: typeLabelMap[nouseMessageList[0].type] })
      : t('fold-message-tips-n', {
          x: typeList
            .slice(0, 3)
            .map((_) => `[${typeLabelMap[_]}]`)
            .join('、'),
          n: nouseMessageList.length,
        });

  const footer = (
    <Footer>
      {tips}
      <Button type="link" onClick={onExpand}>
        {t('review.点击查看')}
      </Button>
    </Footer>
  );

  if (msg.type === 'text') {
    return (
      <LineWrapper layout="right">
        <MessageBody layout="right">
          <Wrapper>
            <TextMsg isFold raw={raw} msg={msg} conversation={conversation} />
          </Wrapper>
          {footer}
        </MessageBody>
      </LineWrapper>
    );
  }

  return (
    <LineWrapper layout="right">
      <MessageBody layout="right">
        <MessageType>
          [
          {t({
            key: `messageType.${raw.messageType}`,
            defaultValue: t('notification.UNKNOWN_MESSAGE_TYPE'),
          })}
          ]
        </MessageType>
        {footer}
      </MessageBody>
    </LineWrapper>
  );
}

const MessageType = styled.div`
  padding: 12px;
  background: #fff;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  color: #454d5a;
  > button {
    padding-left: 8px;
    padding-right: 0;
  }
`;

const Wrapper = styled.div`
  max-width: 400px;
  > div {
    max-width: 100%;
  }
`;

export default memo(FoldMsg);
